/* eslint-disable @typescript-eslint/no-explicit-any */
import { isAuthenticated, setAccessToken } from '@partiu/api/axios';
import { ISubscription } from '@partiu/api/dashboard/types';
import { IUser } from '@partiu/api/types';
import { useProfileSubscriptions, UserHook, useUpdateProfileMutation } from '@partiu/api/user/hooks';
import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useTheme } from './ThemeProvider';
import { useToast } from '@chakra-ui/react';

interface IProfileContext {
  profile: IUser | null;
  subscriptions: ISubscription[];
  isLoading: boolean;
  updateProfile: (data: Record<string, string | boolean>) => void;
  updateProfileImage: (file: File) => void;
  logout: () => void;
}

const defaultProfileContextData: IProfileContext = {
  profile: null,
  subscriptions: [],
  updateProfile: () => {},
  updateProfileImage: () => {},
  logout: () => {},
  isLoading: false,
};

const ProfileContext = createContext(defaultProfileContextData);

export function ProfileProvider({ children }: PropsWithChildren) {
  const [data, setData] = useState(defaultProfileContextData);
  const { id } = useTheme();
  const queryClient = useQueryClient();
  const { data: subscriptions = [] } = useProfileSubscriptions();

  const toast = useToast();

  const onUpdateSuccess = useCallback(
    (profile: IUser | null) => {
      if (!profile) return;

      localStorage.setItem('profile', JSON.stringify(profile));
      queryClient.refetchQueries(UserHook.ProfileSubscriptions);
      setData((data) => ({ ...data, profile }));
    },
    [queryClient]
  );
  const updateProfileMutation = useUpdateProfileMutation({
    onSuccess: onUpdateSuccess,
    onError: (error: any) => {
      const errorMessage =
        Object.values(error.response?.data || {})
          .flat()
          .join(', ') || 'An error occurred';

      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const isLoading = useMemo(() => updateProfileMutation.isLoading, [updateProfileMutation]);
  const updateProfile = useCallback(
    (data: Record<string, string | boolean>) => {
      setData((data) => ({
        ...data,
      }));
      if (!id) return;
      updateProfileMutation.mutate({ ...data, agreementId: id.toString() });
    },
    [updateProfileMutation]
  );

  const updateProfileImage = useCallback(
    (file: File) => {
      const dataform: any = new FormData();
      dataform.append('image', file);
      updateProfileMutation.mutate(dataform);
    },
    [updateProfileMutation]
  );

  useEffect(() => {
    if (!isAuthenticated()) return;
    const savedProfile = JSON.parse(localStorage.getItem('profile') || 'null');
    if (savedProfile) setData((data) => ({ ...data, profile: savedProfile }));

    if (id) updateProfileMutation.mutate({ agreementId: id.toString() });
  }, [id]);

  return (
    <ProfileContext.Provider
      value={{
        ...data,
        subscriptions,
        isLoading: isLoading,
        updateProfile,
        updateProfileImage,
        logout() {
          const existingUrlData = localStorage.getItem('urlData');

          setAccessToken(null);
          localStorage.clear();

          if (existingUrlData) localStorage.setItem('urlData', existingUrlData);

          window.location.reload();
        },
      }}
    >
      {/* <ModalCompleteProfile updateProfile={updateProfile} profileData={data} isOpen={isOpen} onClose={onClose} /> */}
      {children}
    </ProfileContext.Provider>
  );
}

export function useProfile() {
  return useContext(ProfileContext);
}
